import React, { useState, useEffect } from 'react';
import { Box, Typography, Dialog, CircularProgress, LinearProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, CheckCircle2 } from 'lucide-react';

const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const buttonSx = {
  ...FONT,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '12px',
};

const BulkScheduleProgressModal = ({ open, progressData, expectedTotal = 0, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { progress = 0, completed_count = 0, total_tasks = 0, status = 'in_progress' } = progressData || {};
  const isBackendCompleted = status === 'completed' || progress === 100;
  
  const effectiveTotal = total_tasks > 0 ? total_tasks : (expectedTotal > 0 ? expectedTotal : 1);

  const [displayPercent, setDisplayPercent] = useState(0);
  const [showCompletedView, setShowCompletedView] = useState(false);

  useEffect(() => {
    if (!open) {
      setDisplayPercent(0);
      setShowCompletedView(false);
      return;
    }

    let intervalId;

    intervalId = setInterval(() => {
      setDisplayPercent((prev) => {
        const backendPercent = Math.round(progress || 0);

        if (isBackendCompleted) {
          if (prev >= 100) return 100;
          // Step rapidly towards 100
          const step = prev < 50 ? 15 : prev < 80 ? 10 : 6;
          return Math.min(100, prev + step);
        }

        // Progression stages: 0 -> 30% -> 50% -> 70% -> 85% -> 92%
        let increment = 0;
        if (prev < 30) {
          increment = 5;
        } else if (prev < 50) {
          increment = 3;
        } else if (prev < 70) {
          increment = 2.5;
        } else if (prev < 88) {
          increment = 1.5;
        } else if (prev < 92) {
          increment = 0.5;
        }

        const calculated = Math.min(92, prev + increment);
        return Math.max(calculated, Math.min(92, backendPercent));
      });
    }, 180);

    return () => {
      clearInterval(intervalId);
    };
  }, [open, isBackendCompleted, progress]);

  useEffect(() => {
    if (isBackendCompleted && displayPercent >= 100 && !showCompletedView) {
      const timer = setTimeout(() => {
        setShowCompletedView(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [isBackendCompleted, displayPercent, showCompletedView]);

  const effectiveCompleted = showCompletedView
    ? (completed_count || effectiveTotal)
    : Math.min(effectiveTotal, Math.max(0, Math.floor((displayPercent / 100) * effectiveTotal)));

  const handleBackToDashboard = () => {
    onClose();
    navigate('/dashboard');
  };

  const handleDialogClose = (event, reason) => {
    if (!showCompletedView && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={!showCompletedView}
      PaperProps={{
        sx: {
          ...FONT,
          width: { xs: '92%', sm: 480 },
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 60px rgba(15,23,42,0.2)',
        },
      }}
    >
      <Box
        sx={{
          background: GRADIENT,
          px: 2.5,
          py: 2,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.18)',
            flexShrink: 0,
          }}
        >
          <CalendarClock size={18} />
        </Box>
        <Box>
          <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '1.05rem', color: 'white', lineHeight: 1.3 }}>
            {showCompletedView ? t('bulkSchedule.schedulingComplete') : t('bulkSchedule.schedulingProgress')}
          </Typography>
          <Typography sx={{ ...FONT, fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
            {showCompletedView
              ? t('bulkSchedule.schedulingCompleteHint')
              : t('bulkSchedule.schedulingInProgressHint')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 3.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
        {showCompletedView ? (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(22, 163, 74, 0.10)',
                color: '#16a34a',
              }}
            >
              <CheckCircle2 size={36} />
            </Box>
            <Typography sx={{ ...FONT, fontSize: '0.95rem', color: '#475569', textAlign: 'center', lineHeight: 1.6 }}>
              {t('bulkSchedule.interviewsScheduledCount', { completed: effectiveCompleted, total: effectiveTotal })}
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={88}
                thickness={4}
                sx={{ color: '#e2e8f0' }}
              />
              <CircularProgress
                variant="determinate"
                value={displayPercent}
                size={88}
                thickness={4}
                sx={{
                  color: '#0ea5e9',
                  position: 'absolute',
                  left: 0,
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                    transition: 'stroke-dashoffset 0.2s ease',
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ ...FONT, fontWeight: 700, fontSize: '1.15rem', color: '#0f172a' }}>
                  {Math.round(displayPercent)}%
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ ...FONT, fontSize: '0.9rem', color: '#475569', textAlign: 'center' }}>
              {t('bulkSchedule.schedulingForCandidates', { total: effectiveTotal })}
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={displayPercent}
                sx={{
                  height: 10,
                  borderRadius: '8px',
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '8px',
                    background: GRADIENT,
                    transition: 'transform 0.2s ease',
                  },
                }}
              />
              <Typography sx={{ ...FONT, fontSize: '0.75rem', color: '#64748b', mt: 1, textAlign: 'center' }}>
                {t('bulkSchedule.completedOfTotal', { completed: effectiveCompleted, total: effectiveTotal })}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={!showCompletedView}
          sx={{
            ...buttonSx,
            color: '#64748b',
            borderColor: '#e2e8f0',
            borderWidth: '2px',
            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc', borderWidth: '2px' },
            '&.Mui-disabled': { borderColor: '#e2e8f0', color: '#94a3b8' },
          }}
        >
          {t('bulkSchedule.close')}
        </Button>
        <Button
          variant="contained"
          onClick={handleBackToDashboard}
          disabled={!showCompletedView}
          sx={{
            ...buttonSx,
            background: GRADIENT,
            color: 'white',
            boxShadow: 'none',
            '&:hover': {
              opacity: 0.92,
              background: GRADIENT,
              boxShadow: 'none',
            },
            '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
          }}
        >
          {t('bulkSchedule.backToDashboard')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default BulkScheduleProgressModal;
